import React, { useState } from 'react';
import {
  History, Undo2, Loader2, Search,
} from 'lucide-react';
import {
  useGetRachatsAppliquesQuery,
  useAnnulerRachatMutation,
} from '../../services/rachatApi';
import type { RachatApplique } from '../../services/rachatApi';

const HistoriqueRachats: React.FC = () => {
  const [typeFilter, setTypeFilter] = useState('');
  const [searchEtudiant, setSearchEtudiant] = useState('');
  const [confirmAnnulerId, setConfirmAnnulerId] = useState<number | null>(null);

  const queryParams: Record<string, string> = {};
  if (typeFilter) queryParams.type_operation = typeFilter;

  const { data: rawData, isLoading } = useGetRachatsAppliquesQuery(queryParams);
  const rachats: RachatApplique[] = Array.isArray(rawData) ? rawData : (rawData as any)?.results ?? [];
  const [annulerRachat, { isLoading: annulant }] = useAnnulerRachatMutation();

  const filtered = searchEtudiant
    ? rachats.filter(r =>
        r.etudiant_matricule?.toLowerCase().includes(searchEtudiant.toLowerCase()) ||
        r.etudiant_nom?.toLowerCase().includes(searchEtudiant.toLowerCase())
      )
    : rachats;

  const handleAnnuler = async (id: number) => {
    await annulerRachat(id);
    setConfirmAnnulerId(null);
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Type d'opération</label>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Tous</option>
              <option value="rachat">Rachats</option>
              <option value="annulation">Annulations</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Rechercher étudiant</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchEtudiant}
                onChange={e => setSearchEtudiant(e.target.value)}
                placeholder="Matricule, nom..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {filtered.length} enregistrement{filtered.length > 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-blue-500 mr-3" />
            <span className="text-gray-500 dark:text-gray-400">Chargement...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <History size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">Aucun historique de rachat trouvé.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Etudiant</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">UE</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Matière</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Note Exam Avant</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Note Exam Après</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Par</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filtered.map((r: RachatApplique) => (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{r.etudiant_nom}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{r.etudiant_matricule}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{r.ue_code || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-800 dark:text-gray-200">{r.matiere_nom}</div>
                      <div className="text-xs text-gray-400">{r.matiere_code}</div>
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-gray-600 dark:text-gray-300">
                      {parseFloat(r.note_avant).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center font-mono font-semibold">
                      <span className={
                        parseFloat(r.note_apres) > parseFloat(r.note_avant)
                          ? 'text-green-600 dark:text-green-400'
                          : parseFloat(r.note_apres) < parseFloat(r.note_avant)
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-600 dark:text-gray-300'
                      }>
                        {parseFloat(r.note_apres).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        r.type_operation === 'rachat'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                      }`}>
                        {r.type_operation === 'rachat' ? 'Rachat' : 'Annulation'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(r.date_application)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                      {r.applique_par_username || '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {r.type_operation === 'rachat' && (
                        <>
                          {confirmAnnulerId === r.id ? (
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleAnnuler(r.id)}
                                disabled={annulant}
                                className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                              >
                                {annulant ? 'Annulation...' : 'Confirmer'}
                              </button>
                              <button
                                onClick={() => setConfirmAnnulerId(null)}
                                className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                Non
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmAnnulerId(r.id)}
                              title="Annuler ce rachat"
                              className="p-1.5 rounded hover:bg-orange-100 dark:hover:bg-orange-900/30 text-orange-600 dark:text-orange-400"
                            >
                              <Undo2 size={16} />
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoriqueRachats;
